// SidebarViewControls — 사이드바용 화면 컨트롤 한 줄: «PC | 모바일» 전환 + 글자 크기 «− N% +».
// 플로팅 버튼이 본문 UI를 가린다는 피드백으로 사이드바 안으로 이동(로그인 화면은 사이드바가 없어 거기선
// ViewModeToggle 플로팅 유지). 변경 시 setViewMode/setMobileZoom/setDesktopZoom → 전 페이지 즉시 반영·localStorage 유지.
// 글자 크기 = 모바일이면 gwMobileZoom(utils/mobileZoom) · 웹이면 gwDesktopZoom(utils/desktopZoom) — 서로 건드리지 않는다.
// 배치: 사이드바 하단 «OURi와 새 대화» 알약 아래 한 줄 · PC·모바일 공통 · «내 계정» 창엔 없음(10-29 마스터 미리보기 승인).
//   props showToggle(전환 분절) · showZoom(글자 크기 묶음) 은 남긴다(기본 true · 둘 다 false 면 null).
// 🔴 named export 를 두지 않는다 — 여러 시험이 default 만 대역한다(jest.mock(..., () => () => null)).
// 🔴 단추의 바탕·글자색(기본·눌림·hover·disabled)은 인라인이 아니라 아래 <style> 클래스 규칙이 갖는다 — 인라인이면 :hover 규칙이 이기지 못한다.
//   가장 좁은 사이드바(200 · 줄 폭 ≈171)에서도 한 줄이어야 해서 분절 padding 7 · −/+ 22 · % 32 · 간격 4 로 맞췄다.
import { useEffect, useState } from 'react';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import useIsMobile, { setViewMode } from '../hooks/useIsMobile';
import { getMobileZoom, setMobileZoom, ZOOM_MIN, ZOOM_MAX, ZOOM_STEP, MOBILE_ZOOM_EVENT } from '../utils/mobileZoom';
import {
  getDesktopZoom, setDesktopZoom, DESKTOP_ZOOM_MIN, DESKTOP_ZOOM_MAX, DESKTOP_ZOOM_STEP, DESKTOP_ZOOM_EVENT,
} from '../utils/desktopZoom';

const VIEWCTL_CSS = `
.gw-viewctl-row{display:flex;align-items:center;justify-content:space-between;gap:4px;margin-top:8px;height:32px;white-space:nowrap;min-width:0}
.gw-viewctl-seg{display:inline-flex;align-items:center;gap:2px;padding:2px;border:1px solid var(--gw-line,#eeeeec);border-radius:18px;background:var(--gw-surface,#fff);flex-shrink:0}
.gw-viewctl-tab{display:inline-flex;align-items:center;height:26px;padding:0 7px;border:0;border-radius:16px;background:transparent;color:var(--gw-muted,#92918d);font-family:inherit;font-size:12px;cursor:pointer;white-space:nowrap;flex-shrink:0;-webkit-tap-highlight-color:transparent}
.gw-viewctl-tab[aria-pressed=true]{background:var(--gw-gray-soft,#f1f1ef);color:var(--gw-text,#32312e);font-weight:500}
.gw-viewctl-tab:hover:not([aria-pressed=true]){background:var(--gw-hover,#efefed);color:var(--gw-text,#32312e)}
.gw-viewctl-zoom{display:inline-flex;align-items:center;gap:2px;flex-shrink:0}
.gw-viewctl-zbtn{width:22px;height:22px;display:inline-flex;align-items:center;justify-content:center;padding:0;border:0;border-radius:5px;background:transparent;color:var(--gw-muted,#92918d);cursor:pointer;flex-shrink:0;-webkit-tap-highlight-color:transparent}
.gw-viewctl-zbtn:hover:not(:disabled){background:var(--gw-hover,#efefed);color:var(--gw-text,#32312e)}
.gw-viewctl-zbtn:disabled{opacity:.4;cursor:default}
.gw-viewctl-pct{min-width:32px;text-align:center;font-size:12px;color:var(--gw-secondary,#64635f);font-variant-numeric:tabular-nums}
`;

export default function SidebarViewControls({ showToggle = true, showZoom = true }) {
  const isMobile = useIsMobile();
  const [mobileZoom, setMobileZoomState] = useState(getMobileZoom);
  const [desktopZoom, setDesktopZoomState] = useState(getDesktopZoom);
  useEffect(() => {
    const s = () => setMobileZoomState(getMobileZoom());
    const d = () => setDesktopZoomState(getDesktopZoom());
    const both = () => { s(); d(); };
    window.addEventListener(MOBILE_ZOOM_EVENT, s);
    window.addEventListener(DESKTOP_ZOOM_EVENT, d);
    window.addEventListener('storage', both);
    return () => {
      window.removeEventListener(MOBILE_ZOOM_EVENT, s);
      window.removeEventListener(DESKTOP_ZOOM_EVENT, d);
      window.removeEventListener('storage', both);
    };
  }, []);
  const zoom = isMobile ? mobileZoom : desktopZoom;
  const zoomMin = isMobile ? ZOOM_MIN : DESKTOP_ZOOM_MIN;
  const zoomMax = isMobile ? ZOOM_MAX : DESKTOP_ZOOM_MAX;
  const zoomStep = isMobile ? ZOOM_STEP : DESKTOP_ZOOM_STEP;
  const applyZoom = isMobile ? setMobileZoom : setDesktopZoom;
  const atMin = zoom <= zoomMin + 0.001;
  const atMax = zoom >= zoomMax - 0.001;
  const pct = Math.round(zoom * 100);
  // 이미 눌린 쪽을 누르면 아무것도 안 한다(저장값·이벤트 그대로).
  const pick = (mode) => { if ((mode === 'mobile') !== isMobile) setViewMode(mode); };
  if (!showToggle && !showZoom) return null;
  return (
    <div className="gw-viewctl-row" data-testid="sidebar-view-controls">
      <style>{VIEWCTL_CSS}</style>
      {showToggle && (
        <div className="gw-viewctl-seg" role="group" aria-label="화면 보기" title="웹/모바일 화면 전환">
          <button type="button" className="gw-viewctl-tab" aria-pressed={!isMobile} onClick={() => pick('desktop')}>PC</button>
          <button type="button" className="gw-viewctl-tab" aria-pressed={isMobile} onClick={() => pick('mobile')}>모바일</button>
        </div>
      )}
      {showZoom && (
        <div className="gw-viewctl-zoom" role="group" aria-label="글자 크기" title={isMobile ? '모바일 글자 크기' : '웹 글자 크기'}>
          <button type="button" className="gw-viewctl-zbtn" aria-label="글자 작게" onClick={() => applyZoom(zoom - zoomStep)} disabled={atMin}><IconMinus size={14} stroke={1.65} /></button>
          <span className="gw-viewctl-pct" aria-live="polite">{pct}%</span>
          <button type="button" className="gw-viewctl-zbtn" aria-label="글자 크게" onClick={() => applyZoom(zoom + zoomStep)} disabled={atMax}><IconPlus size={14} stroke={1.65} /></button>
        </div>
      )}
    </div>
  );
}
